// Card #2826 (docs/score/scribe-resilience.md gap G12 part A) — the
// aggregation behind the /outbox page: one row per SCORE SHEET (a
// rideId::judgeId pair) this station has ever touched for the active show,
// shipped and unshipped alike, never one row per raw outbox op.
//
// Deliberately pure: given the two op lists sync.js's engine already
// exposes (listAllOps/listSurfacedOps) plus the local caches, it derives
// rows with no network access of its own — the whole point of AC2 (the
// /outbox page paints entirely from IndexedDB, offline included). The
// caller (Outbox.jsx) is the one that calls syncEngine.listAllOps() /
// listSurfacedOps() and hands the results in; this module never imports
// sync.js, so it can be unit tested against hand-built op arrays without
// spinning up the full engine.
import {
  classes,
  isDivergedFromSyncedAncestor,
  isLocalDraftScoreSheet,
  rides,
  scoreSheets,
  shipLog,
  tombstones,
} from "./db";
import { REVIEW_OP_TYPE } from "./reviewRequests";
import { describeOutboxOp, isHeldForDisplay } from "./outboxDetail";
import { getFailureAction } from "./failureActions";

export const OUTBOX_SHEET_STATE = Object.freeze({
  HELD: "held",
  NEEDS_ATTENTION: "needsAttention",
  SENDING: "sending",
  QUEUED: "queued",
  SHIPPED: "shipped",
  IN_REVIEW: "inReview",
  // Card #3015 (split from #2826 AC1) -- a sheet the #2810 pre-flight
  // blocked from ever reaching the outbox: persist() (useScoreSheet.js)
  // still commits it to the local scoreSheets cache, it just never enqueues
  // an op for it, so it had no row at all here before this state existed.
  DRAFT: "draft",
  // Card #3014 -- a sheet whose op was discarded or quarantined leaves a
  // tombstone (db.js recordTombstone) instead of just vanishing.
  DISCARDED: "discarded",
  QUARANTINED: "quarantined",
  // Card #3012 -- a shipped sheet whose ship-log row carries a failureCode
  // (its op failed at least once before it went on to succeed) shows as
  // resolved with the cause, not indistinguishable from a sheet that shipped
  // clean on the first try.
  RESOLVED: "resolved",
  // #3088 -- the ride behind this row was sent to the review queue with the
  // scribe's REVIEW button. Replaces the sheet's normal queued/sending/
  // shipped state, exactly as the In Review badge replaces the ride's status
  // on the ride list. Distinct from IN_REVIEW above (a confirmed-not-signed
  // sheet) and QUARANTINED (a discarded op): this is a ride-level fact.
  RIDE_IN_REVIEW: "rideInReview",
});

// #3088 -- states that already say something the scribe must act on or know
// about; a ride-level review request never hides them.
const KEEP_OVER_RIDE_REVIEW = new Set([
  OUTBOX_SHEET_STATE.HELD,
  OUTBOX_SHEET_STATE.NEEDS_ATTENTION,
  OUTBOX_SHEET_STATE.DISCARDED,
  OUTBOX_SHEET_STATE.QUARANTINED,
]);

// Checked in this order for a single op — a held row is blocked on
// ownership, not on the failure a "needs attention" problem/action pair
// describes, so held is decided first (design.md). Sending/queued are the
// two remaining "nothing wrong, just waiting" states for an op that is
// neither held nor surfaced.
function stateForOp(op, surfacedOpIds) {
  if (isHeldForDisplay(op)) return OUTBOX_SHEET_STATE.HELD;
  if (surfacedOpIds.has(op.opId)) return OUTBOX_SHEET_STATE.NEEDS_ATTENTION;
  if (op.inFlight) return OUTBOX_SHEET_STATE.SENDING;
  return OUTBOX_SHEET_STATE.QUEUED;
}

// A sheet entity's outbox op (createScoreSheet/updateScoreSheet) shares one
// opId with any later save of the same sheet ("upsert per entityKey"), but
// completeScoreSheet/confirmScoreSheetMarks/panelSignScoreSheet each carry
// their OWN opId against the SAME entityKey — so more than one op can be
// queued for one sheet at once (e.g. an update queued, then a confirm,
// before either has shipped). When that happens the row shows whichever
// op's state is most useful for a scribe to see: a real problem outranks a
// hold (a hold is just waiting on the right scribe), which outranks an
// in-flight send, which outranks a plain queue.
const STATE_PRIORITY = {
  [OUTBOX_SHEET_STATE.NEEDS_ATTENTION]: 3,
  [OUTBOX_SHEET_STATE.HELD]: 2,
  [OUTBOX_SHEET_STATE.SENDING]: 1,
  [OUTBOX_SHEET_STATE.QUEUED]: 0,
};

function pickRepresentativeOp(ops, surfacedOpIds) {
  let best = null;
  let bestPriority = -1;
  for (const op of ops) {
    const state = stateForOp(op, surfacedOpIds);
    const priority = STATE_PRIORITY[state];
    if (priority > bestPriority) {
      best = { op, state };
      bestPriority = priority;
    }
  }
  return best;
}

// A sheet op's entityKey is always `${rideId}::${judgeId}` (useScoreSheet.js
// enqueue calls); createRide's entityKey is a bare tempId and a utility-event
// op's is a bare rideId — neither ever contains "::". No hardcoded op-type
// list: any current or future sheet op type is picked up by this shape
// check alone (design.md).
function isSheetEntityKey(entityKey) {
  return typeof entityKey === "string" && entityKey.includes("::");
}

function parseEntityKey(entityKey) {
  const at = entityKey.indexOf("::");
  return { rideId: entityKey.slice(0, at), judgeId: entityKey.slice(at + 2) };
}

// Builds a descriptor for a sheet that has ONLY ship history left (no
// current outbox op) — describeOutboxOp expects an op-shaped object, so this
// synthesizes the minimal shape it actually reads (payload.rideId/judgeId)
// straight from the entityKey. outboxDetail.js is never edited for this
// (design.md); it already tolerates a ride/class/show that isn't cached by
// returning nulls, so a legacy or evicted entity behaves the same way here.
async function describeShippedEntity(entityKey) {
  const { rideId, judgeId } = parseEntityKey(entityKey);
  return describeOutboxOp({
    opId: `shipped:${entityKey}`,
    type: "shipped",
    entityKey,
    status: "shipped",
    payload: { rideId, judgeId },
  });
}

// Card #3015 -- same synthetic-op trick as describeShippedEntity above, for a
// scoreSheets row that is a local draft (isLocalDraftScoreSheet) with
// neither a current outbox op nor any ship history for its entityKey.
async function describeDraftEntity(entityKey) {
  const { rideId, judgeId } = parseEntityKey(entityKey);
  return describeOutboxOp({
    opId: `draft:${entityKey}`,
    type: "draft",
    entityKey,
    status: "draft",
    payload: { rideId, judgeId },
  });
}

// Card #3014 -- same synthetic-op trick as describeShippedEntity/
// describeDraftEntity above, for an entityKey whose only trace left is a
// tombstone (recordTombstone, db.js): no current outbox op and no ship
// history, because a discard or quarantine deletes the outbox row in the
// same transaction that writes the tombstone.
async function describeTombstonedEntity(entityKey) {
  const { rideId, judgeId } = parseEntityKey(entityKey);
  return describeOutboxOp({
    opId: `tombstone:${entityKey}`,
    type: "tombstone",
    entityKey,
    status: "tombstone",
    payload: { rideId, judgeId },
  });
}

// Card #3015 -- a cached scoreSheets row's entityKey, the SAME
// `${rideId}::${judgeId}` shape as an outbox op's (useScoreSheet.js enqueue
// calls) and putAllFromServer's own entityKeyOf (db.js). Every scoreSheets
// row this module ever writes -- server-hydrated, provisional, or a blocked
// update's local commit -- carries both fields (persist() always sets
// rideId/judgeId on the row it puts), so this is never guessed from `id`.
function entityKeyForRow(row) {
  return row?.rideId && row?.judgeId ? `${row.rideId}::${row.judgeId}` : null;
}

// AC's "In review" refinement: a shipped-only sheet whose cached scoreSheet
// row shows marks confirmed but not yet completed. Falls back to plain
// Shipped whenever the ride/judge can't be resolved or no scoreSheets row is
// cached for it (offline cache miss) — never throws.
//
// Card #3012 -- checked FIRST, ahead of the in-review/shipped split: a
// shipRow carrying a failureCode means the sheet's last op failed at least
// once before it went on to ship (db.js's recordShipment stamps this
// additively from the op's own pre-ship attempts/failureCode/errorClass).
// That is a more relevant fact for a scribe glancing at /outbox than the
// marks-confirmed distinction below, and unlike IN_REVIEW/SHIPPED it never
// silently reverts once the underlying cause clears -- a shipLog row is
// immutable history, not live state.
// #3025 -- the cached scoreSheets row for a shipped-bucket entityKey
// (Shipped/In review/Resolved), fetched once here rather than separately by
// refineShippedState and unsentEditNoteFor below -- same
// byRideJudge(rideId, judgePosition) lookup refineShippedState always ran on
// its own, now shared.
async function localScoreSheetRowFor(descriptor) {
  if (!descriptor.rideId || !descriptor.judgePosition) return null;
  const rows = await scoreSheets
    .byRideJudge(descriptor.rideId, descriptor.judgePosition)
    .catch(() => []);
  return rows?.[0] ?? null;
}

async function refineShippedState(descriptor, shipRow, localRow) {
  if (shipRow?.failureCode) {
    return OUTBOX_SHEET_STATE.RESOLVED;
  }
  if (localRow?.marksConfirmedAt && !localRow?.completedAt) {
    return OUTBOX_SHEET_STATE.IN_REVIEW;
  }
  return OUTBOX_SHEET_STATE.SHIPPED;
}

// #3025 -- a shipped-bucket row's cached scoreSheets content can diverge from
// its own tracked ancestor (`isDivergedFromSyncedAncestor`, db.js) the SAME
// way a never-shipped draft's does (#3015): a later edit against an
// already-shipped sheet that the #2810 pre-flight blocked never enqueues an
// op, so it leaves no trace in opsByEntityKey and this entityKey stays in
// the shipped bucket. Before this card, the draft sweep further down
// actively skipped any entityKey already in latestShipmentByEntityKey (see
// its own comment below), so a diverged edit on a shipped sheet produced NO
// row at all for the edit -- /outbox showed plain Shipped/In review with the
// unsent edit invisible. Fix keeps the ship-history state and shippedAt
// intact (AC2: a shipped sheet never edited after its last ack is
// unaffected, since a row with no `_syncedSheet` or matching content is
// never diverged) and surfaces the edit as a problem-column note (AC1),
// rather than reclassifying the row as Draft the way isLocalDraftScoreSheet
// would -- that would lose shippedAt and contradict AC2's "still shows
// Shipped/In review" for the unedited case by conflating the two cases under
// one state. This never touches tombstone-vs-draft precedence (#3014): a
// tombstoned entityKey is never in latestShipmentByEntityKey to begin with.
const UNSENT_LOCAL_EDIT_NOTE = "Unsent local edit (blocked by pre-flight)";

function unsentEditNoteFor(localRow) {
  return isDivergedFromSyncedAncestor(localRow) ? UNSENT_LOCAL_EDIT_NOTE : null;
}

// classId -> showId, resolved through the SAME cache describeOutboxOp itself
// reads (classes.get), so a class this station never cached simply can't be
// scoped to a show (falls through to "unresolved", see below) rather than
// throwing. describeOutboxOp already exposes showName but not the raw
// showId a caller could filter on — resolving it here, rather than editing
// outboxDetail.js, keeps that module's return shape stable for its other
// two callers (SyncStatus.jsx, the #1921 export).
async function resolveShowId(descriptor) {
  if (!descriptor.classId) return null;
  const klass = await classes.get(descriptor.classId).catch(() => null);
  return klass?.showId ?? null;
}

function rowFromDescriptor({ descriptor, opId, state, shippedAt, problem, actionChoices }) {
  return {
    key: descriptor.entityKey ?? `${descriptor.rideId}::${descriptor.judgePosition}`,
    opId,
    rideId: descriptor.rideId,
    classId: descriptor.classId,
    classNumber: descriptor.classNumber,
    className: descriptor.className,
    competitorNumber: descriptor.competitorNumber,
    horseName: descriptor.horseName,
    riderName: descriptor.riderName,
    judgePosition: descriptor.judgePosition,
    showName: descriptor.showName,
    rideScheduledTime: descriptor.rideScheduledTime,
    state,
    shippedAt: shippedAt ?? null,
    problem: problem ?? null,
    // Raw ACTIONS.* ids (failureActions.js), never translated text -- see
    // problemAndActionFor.
    actionChoices: actionChoices ?? [],
  };
}

// Human, scribe-language problem for a needs-attention row, off C3's
// failure-action table (getFailureAction) rather than a bare code. The
// CHOICES themselves (ACTIONS.RETRY/.DISCARD/.QUARANTINE, failureActions.js)
// are returned as their raw ids, never translated here — this module is
// pure/unit-tested with no i18n dependency, so the caller (Outbox.jsx) is
// the one that maps each id to a static literal locale key and renders it
// as plain text, never a button (this page is read-only; AC4/"send snapshot
// now" split to #3012).
function problemAndActionFor(descriptor) {
  const failure = getFailureAction(descriptor.failureCode);
  return { problem: failure.title, actionChoices: failure.choices ?? [] };
}

/**
 * Aggregates every sheet (rideId::judgeId) this station has touched into one
 * row each, shipped and unshipped alike (AC1).
 *
 * @param {object} args
 * @param {object[]} args.allOps - syncEngine.listAllOps() (the full current
 *   outbox, unfiltered).
 * @param {object[]} args.surfacedOps - syncEngine.listSurfacedOps() (the
 *   stuck/held subset) — used only to decide which of allOps' ops count as
 *   "needs attention".
 * @param {string} [args.showId] - when given, a row whose class DOES
 *   resolve to a different show is dropped. A row whose show can't be
 *   resolved at all (evicted/never-cached class) is kept rather than
 *   guessed away — hiding a real unshipped sheet because its class fell out
 *   of cache would be worse than showing it unscoped.
 */
export async function buildOutboxSheetRows({ allOps = [], surfacedOps = [], showId } = {}) {
  const sheetOps = allOps.filter((op) => isSheetEntityKey(op.entityKey));
  const surfacedOpIds = new Set(
    surfacedOps.filter((op) => isSheetEntityKey(op.entityKey)).map((op) => op.opId),
  );

  const opsByEntityKey = new Map();
  for (const op of sheetOps) {
    const group = opsByEntityKey.get(op.entityKey);
    if (group) group.push(op);
    else opsByEntityKey.set(op.entityKey, [op]);
  }

  // Ship history only ever fills a row for an entityKey with NO current
  // outbox op — a current op supersedes ship history (design.md), because
  // the sheet has moved on (e.g. re-opened and re-queued after a fix).
  const shippedRows = (await shipLog.getAll()).filter((row) => isSheetEntityKey(row.entityKey));
  const latestShipmentByEntityKey = new Map();
  for (const row of shippedRows) {
    if (opsByEntityKey.has(row.entityKey)) continue;
    const existing = latestShipmentByEntityKey.get(row.entityKey);
    if (!existing || (row.acknowledgedAt ?? "") > (existing.acknowledgedAt ?? "")) {
      latestShipmentByEntityKey.set(row.entityKey, row);
    }
  }

  // Card #3014 -- a tombstone only ever fills a row for an entityKey with NO
  // current outbox op and NO ship history: a live current op wins over a
  // tombstone (it's a later op against the same sheet, possibly one that
  // will succeed or ship), and a real ship-history row wins over a
  // tombstone too (same reasoning as the ship-vs-current-op precedence just
  // above this comment) -- a tombstone is written for one particular
  // discarded/quarantined op instance and never precludes a LATER op
  // against the same entityKey.
  const tombstonesByEntityKey = new Map();
  for (const row of await tombstones.getAll()) {
    if (!isSheetEntityKey(row.entityKey)) continue;
    if (opsByEntityKey.has(row.entityKey) || latestShipmentByEntityKey.has(row.entityKey)) continue;
    tombstonesByEntityKey.set(row.entityKey, row);
  }

  const rows = [];

  for (const [entityKey, ops] of opsByEntityKey) {
    const { op, state } = pickRepresentativeOp(ops, surfacedOpIds);
    const descriptor = await describeOutboxOp(op);
    const showIdForRow = await resolveShowId(descriptor);
    if (showId && showIdForRow && showIdForRow !== showId) continue;
    const { problem, actionChoices } =
      state === OUTBOX_SHEET_STATE.NEEDS_ATTENTION
        ? problemAndActionFor(descriptor)
        : { problem: null, actionChoices: [] };
    rows.push(
      rowFromDescriptor({
        descriptor: { ...descriptor, entityKey },
        opId: op.opId,
        state,
        problem,
        actionChoices,
      }),
    );
  }

  for (const [entityKey, shipRow] of latestShipmentByEntityKey) {
    const descriptor = await describeShippedEntity(entityKey);
    const showIdForRow = await resolveShowId(descriptor);
    if (showId && showIdForRow && showIdForRow !== showId) continue;
    const localRow = await localScoreSheetRowFor(descriptor);
    const state = await refineShippedState(descriptor, shipRow, localRow);
    // Card #3012 -- same failureCode -> title lookup problemAndActionFor
    // uses for a live needs-attention row (getFailureAction), but with no
    // actionChoices: the op already shipped, there is nothing left to act on.
    const failureProblem =
      state === OUTBOX_SHEET_STATE.RESOLVED
        ? getFailureAction(shipRow.failureCode).title
        : null;
    // #3025 -- appended (never replaces) the RESOLVED failure title: the two
    // facts are independent (a past failed attempt vs. a current unsent
    // edit) and a row can carry both.
    const unsentEditNote = unsentEditNoteFor(localRow);
    const problem = [failureProblem, unsentEditNote].filter(Boolean).join("; ") || null;
    rows.push(
      rowFromDescriptor({
        descriptor: { ...descriptor, entityKey },
        opId: shipRow.opId,
        state,
        shippedAt: shipRow.acknowledgedAt ?? null,
        problem,
      }),
    );
  }

  // Card #3015 (split from #2826 AC1) -- a sheet the #2810 pre-flight
  // blocked from the outbox has NEITHER a current outbox op (opsByEntityKey,
  // above -- persist() never enqueues one for a blocked verdict) NOR ship
  // history (latestShipmentByEntityKey -- it never shipped either), so
  // neither loop above ever builds it a row: it was invisible on /outbox
  // before this. Its only trace is the local scoreSheets cache row
  // persist()'s own local commit still writes unconditionally (AC4 of
  // #2810 -- pre-flight never blocks the local save). Scanning every cached
  // sheet is the only way to find one: there is nothing else (no op, no
  // ship row) to start from.
  // #3025 -- an entityKey WITH ship history is deliberately still excluded
  // here even when its cached row has since diverged (a later edit the
  // pre-flight blocked): that case keeps its Shipped/In review/Resolved row
  // from the loop above (shippedAt intact, AC2), carrying the divergence as
  // an "unsent local edit" problem-column note (unsentEditNoteFor) instead
  // of being reclassified as Draft here, which would lose shippedAt. See
  // that loop's own #3025 comment for the full reasoning.
  const draftEntityKeys = new Set();
  for (const row of await scoreSheets.getAll()) {
    const entityKey = entityKeyForRow(row);
    if (!entityKey || !isSheetEntityKey(entityKey)) continue;
    if (opsByEntityKey.has(entityKey) || latestShipmentByEntityKey.has(entityKey)) continue;
    // Card #3014 -- a tombstone takes precedence over Draft: the scoreSheets
    // row a discard/quarantine leaves behind is stale evidence of what the
    // sheet USED to hold, but the sheet's actual last-known state is the
    // tombstone (Discarded/In review), not "still a draft".
    if (tombstonesByEntityKey.has(entityKey)) continue;
    if (!isLocalDraftScoreSheet(row)) continue;
    draftEntityKeys.add(entityKey);
  }

  for (const entityKey of draftEntityKeys) {
    const descriptor = await describeDraftEntity(entityKey);
    const showIdForRow = await resolveShowId(descriptor);
    if (showId && showIdForRow && showIdForRow !== showId) continue;
    rows.push(
      rowFromDescriptor({
        descriptor: { ...descriptor, entityKey },
        opId: `draft:${entityKey}`,
        state: OUTBOX_SHEET_STATE.DRAFT,
      }),
    );
  }

  // Card #3014 -- render one row per tombstoned entity (Discarded/In
  // review), each carrying its own when + cause. Reuses the existing
  // shippedAt/problem row fields rather than adding new ones: shippedAt
  // already means "when this row's current state took effect" and problem
  // already means "why this row needs a look" for other states, and a
  // tombstone's at/cause are the same kind of fact — Outbox.jsx's
  // problemCellText already renders any truthy row.problem regardless of
  // state, so no Outbox.jsx column change is needed for this.
  for (const [entityKey, tomb] of tombstonesByEntityKey) {
    const descriptor = await describeTombstonedEntity(entityKey);
    const showIdForRow = await resolveShowId(descriptor);
    if (showId && showIdForRow && showIdForRow !== showId) continue;
    rows.push(
      rowFromDescriptor({
        descriptor: { ...descriptor, entityKey },
        opId: `tombstone:${entityKey}`,
        state:
          tomb.kind === "quarantined"
            ? OUTBOX_SHEET_STATE.QUARANTINED
            : OUTBOX_SHEET_STATE.DISCARDED,
        shippedAt: tomb.at ?? null,
        problem: tomb.cause ?? null,
      }),
    );
  }

  // #3088 -- the ride-level In Review flag. A ride is in review when its
  // cached row carries an active reviewRequest (optimistic or server) or a
  // review op is still queued for it. Every sheet row of such a ride shows In
  // Review instead of queued/sending/shipped/etc, and a ride whose only trace
  // here is the queued review op gets a row of its own so the press is
  // visible in the outbox even before any sheet exists.
  const reviewOps = allOps.filter((op) => op.type === REVIEW_OP_TYPE);
  const reviewOpByRide = new Map(reviewOps.map((op) => [op.payload?.rideId, op]));
  const inReviewRideIds = new Set(reviewOpByRide.keys());
  const rideIdsWithRows = new Set(rows.map((r) => r.rideId));
  for (const rideId of rideIdsWithRows) {
    if (!rideId || inReviewRideIds.has(rideId)) continue;
    const cachedRide = await rides.get(rideId).catch(() => null);
    if (cachedRide?.reviewRequest?.active === true) inReviewRideIds.add(rideId);
  }
  for (const row of rows) {
    if (inReviewRideIds.has(row.rideId) && !KEEP_OVER_RIDE_REVIEW.has(row.state)) {
      row.state = OUTBOX_SHEET_STATE.RIDE_IN_REVIEW;
    }
  }
  for (const [rideId, op] of reviewOpByRide) {
    if (!rideId || rideIdsWithRows.has(rideId)) continue;
    const descriptor = await describeOutboxOp(op);
    const showIdForRow = await resolveShowId(descriptor);
    if (showId && showIdForRow && showIdForRow !== showId) continue;
    const opState = stateForOp(op, surfacedOpIds);
    const state = KEEP_OVER_RIDE_REVIEW.has(opState) ? opState : OUTBOX_SHEET_STATE.RIDE_IN_REVIEW;
    rows.push({
      ...rowFromDescriptor({
        descriptor: { ...descriptor, entityKey: `review:${rideId}` },
        opId: op.opId,
        state,
        problem: state === OUTBOX_SHEET_STATE.NEEDS_ATTENTION ? problemAndActionFor(descriptor).problem : null,
        actionChoices: state === OUTBOX_SHEET_STATE.NEEDS_ATTENTION ? problemAndActionFor(descriptor).actionChoices : [],
      }),
    });
  }

  rows.sort((a, b) => {
    const classA = Number(a.classNumber);
    const classB = Number(b.classNumber);
    if (Number.isFinite(classA) && Number.isFinite(classB) && classA !== classB) {
      return classA - classB;
    }
    const cnoA = Number(a.competitorNumber);
    const cnoB = Number(b.competitorNumber);
    if (Number.isFinite(cnoA) && Number.isFinite(cnoB) && cnoA !== cnoB) {
      return cnoA - cnoB;
    }
    return (a.rideScheduledTime ?? "").localeCompare(b.rideScheduledTime ?? "");
  });

  return rows;
}
